import React, { useContext, useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import CartContext from '../context/CartContext';
import CurrencySwitcher from './CurrencySwitcher';
import CartDrawer from './CartDrawer';

const Navbar = () => {
    const { user, logout, isAuthenticated } = useContext(AuthContext);
    const { cart } = useContext(CartContext);
    const [isOpen, setIsOpen] = useState(false);
    const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Total units, not distinct lines: adding 3 of something already in the
    // cart has to move this number, or the badge would sit still and the
    // animation would look broken.
    const itemCount = cart?.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

    const [bump, setBump] = useState(false);
    const prevCount = useRef(itemCount);

    useEffect(() => {
        if (itemCount > prevCount.current) {
            setBump(true);
            const timer = setTimeout(() => setBump(false), 450);
            prevCount.current = itemCount;
            return () => clearTimeout(timer);
        }
        prevCount.current = itemCount;
    }, [itemCount]);

    const badgeClass = `absolute -top-1.5 -right-1.5 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium ${bump ? 'animate-cart-bump' : ''}`;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const navClass = scrolled || isOpen
        ? 'shadow-2xl shadow-black/60'
        : 'bg-transparent border-b border-white/0';
        const scrolledStyle = (scrolled || isOpen) ? { background: 'rgba(10,10,20,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.08)' } : {};
    const navLink = "text-gray-300 hover:text-white font-light tracking-wide transition-all duration-300 text-sm relative group";

    return (
        <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${navClass}`} style={scrolledStyle}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center group">
                        <img src="/logo.svg" alt="DOCTAPE" className="h-7 w-auto group-hover:opacity-80 transition-opacity duration-300" />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        {[
                            { to: '/', label: 'Home' },
                            { to: '/catalog', label: 'Products' },
                            { to: '/training-guides', label: 'Guides' },
                            { to: '/about', label: 'About' },
                        ].map(({ to, label }) => (
                            <Link key={to} to={to} className={navLink}>
                                {label}
                                <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-blue-400 group-hover:w-full transition-all duration-300 rounded-full"></span>
                            </Link>
                        ))}
                    </div>

                    {/* Right Section */}
                    <div className="hidden md:flex items-center space-x-5">
                        <CurrencySwitcher />

                        {/* Cart */}
                        <button
                            onClick={() => setIsCartDrawerOpen(true)}
                            className="relative flex items-center text-gray-300 hover:text-white transition-all duration-300 group p-1"
                            aria-label="Open cart"
                        >
                            <svg className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                            </svg>
                            {itemCount > 0 && (
                                <span className={`${badgeClass} shadow-lg shadow-blue-500/40`}>
                                    {itemCount}
                                </span>
                            )}
                        </button>

                        {/* Auth */}
                        {isAuthenticated ? (
                            <div className="flex items-center space-x-3 border-l border-white/10 pl-5">
                                <span className="text-gray-300 font-light text-sm">{user?.firstName}</span>
                                <button
                                    onClick={logout}
                                    className="text-sm text-gray-300 hover:text-white px-4 py-2 rounded-lg border border-white/20 hover:border-white/40 hover:bg-white/5 transition-all duration-300 font-light"
                                >
                                    Logout
                                </button>
                            </div>
                        ) : (
                            <Link
                                to="/login"
                                className="text-sm font-medium text-white px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 transition-all duration-300 shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40"
                            >
                                Login
                            </Link>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="md:hidden flex items-center gap-4">
                        {/* Mobile Cart Button */}
                        <button
                            onClick={() => setIsCartDrawerOpen(true)}
                            className="relative flex items-center text-gray-300 hover:text-white transition-colors duration-300"
                            aria-label="Open cart"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                            </svg>
                            {itemCount > 0 && (
                                <span className={badgeClass}>
                                    {itemCount}
                                </span>
                            )}
                        </button>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="text-white p-2 hover:text-blue-400 transition-all duration-300"
                            aria-label="Toggle menu"
                        >
                            {isOpen ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <div className={`md:hidden overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="glass-dark border-t border-white/5 px-4 py-6 space-y-4">
                    {[
                        { to: '/', label: 'Home' },
                        { to: '/catalog', label: 'Products' },
                        { to: '/training-guides', label: 'Guides' },
                        { to: '/about', label: 'About' },
                    ].map(({ to, label }) => (
                        <Link
                            key={to}
                            to={to}
                            onClick={() => setIsOpen(false)}
                            className="block text-gray-300 hover:text-white font-light tracking-wide text-sm py-1 border-b border-white/5 hover:border-blue-500/30 transition-all duration-300"
                        >
                            {label}
                        </Link>
                    ))}
                    <div className="pt-3 flex items-center justify-between">
                        <CurrencySwitcher />
                        {!isAuthenticated && (
                            <Link to="/login" onClick={() => setIsOpen(false)} className="text-sm font-medium text-white px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 transition-all duration-300">
                                Login
                            </Link>
                        )}
                    </div>
                </div>
            </div>

            {/* Cart Drawer */}
            <CartDrawer isOpen={isCartDrawerOpen} onClose={() => setIsCartDrawerOpen(false)} />
        </nav>
    );
};

export default Navbar;
